import { ArrowRight, Check, ChevronRight, Clock3 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { RegulatedNote, photoBackground } from "../../components/Cards";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { formatTimestamp } from "../../lib/format";
import { formatMxn } from "../../lib/loans";
import { useLoanConfig, useMyLoanApplications, useProducts } from "../../lib/queries";
import type { LoanApplication } from "../../types";
import { LoanStatusTag } from "./LoanShared";

const HERO_SHADE = "linear-gradient(180deg, rgba(0,37,87,0.55) 0%, rgba(0,37,87,0.25) 30%, rgba(0,37,87,0.88) 70%, rgba(0,37,87,0.97) 100%)";
const BENEFITS = ["Sin aval", "Sin garantías", "Proceso 100% digital", "Respuesta rápida", "Tasa fija durante todo el plazo"];

/**
 * "Viaja hoy, paga después" — the page before the application (design 4a/4b): the offer, the
 * requirements, and the customer's own applications. `Comenzar` opens the two-step form; while an
 * application is open the primary action becomes following it instead.
 */
export function LoanEntryPage() {
  usePageHeader("Viaja hoy, paga después", "Crédito para tu viaje con Peso a Peso");
  const navigate = useNavigate();
  const config = useLoanConfig();
  const products = useProducts();
  const applications = useMyLoanApplications();
  const product = products.data?.products.find((item) => item.id === "tnpl");

  if (config.isLoading || applications.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (!config.data?.enabled) {
    return (
      <div className="page-container">
        <section className="card coming-card">
          <span className="benefit-icon"><Clock3 size={18} aria-hidden="true" /></span>
          <div>
            <strong>Disponible próximamente</strong>
            <p>Viaja ahora, paga después aún no está habilitado para tu cuenta.</p>
          </div>
          <Link className="secondary-button" to="/wallet">Volver a Finanzas</Link>
        </section>
      </div>
    );
  }

  const settings = config.data;
  const list = applications.data ?? [];
  const open = list.find((application) => application.status === "submitted" || application.status === "in_review") ?? null;
  const rate = `${settings.annualRatePercent}%`;

  return (
    <div className="page-container">
      <section className="loan-hero" style={photoBackground(product?.photoUrl, HERO_SHADE, "wide")}>
        <div className="loan-hero-copy">
          <span className="loan-kicker">Viaja ahora · paga después</span>
          <h2>Haz ese viaje realidad</h2>
          <p className="loan-hero-sub">Nosotros te ayudamos a financiarlo</p>
          <ul className="loan-benefits">
            {BENEFITS.map((benefit) => (
              <li key={benefit}><span className="loan-benefit-check"><Check size={12} strokeWidth={3} aria-hidden="true" /></span>{benefit}</li>
            ))}
          </ul>
        </div>
        <div className="loan-offer-panel">
          <span className="loan-offer-label">Solicita hasta</span>
          <div className="loan-offer-amount" translate="no"><strong>${settings.maxAmount.toLocaleString("en-US")}</strong><span>MXN</span></div>
          <p>Desde {formatMxn(settings.minAmount)}, en plazos de {settings.termOptions.join(" o ")} meses.</p>
          <dl className="loan-offer-facts">
            <div><dt>Tasa</dt><dd>{rate} anual fija</dd></div>
            <div><dt>Plazos</dt><dd>{settings.termOptions.join(" o ")} meses</dd></div>
            <div><dt>Requisitos</dt><dd>CURP y constancia de situación fiscal</dd></div>
          </dl>
          {open ? (
            <button type="button" className="primary-button full loan-cta waikiki" onClick={() => navigate(`/credito/solicitudes/${open.id}`)}>
              Ver estatus de mi solicitud <ArrowRight size={16} aria-hidden="true" />
            </button>
          ) : (
            <button type="button" className="primary-button full loan-cta waikiki" onClick={() => navigate("/credito/solicitud")}>
              Comenzar <ArrowRight size={16} aria-hidden="true" />
            </button>
          )}
          <p className="loan-offer-legal">Tasa {rate} anual fija. El crédito lo otorga {settings.lenderName} Aventuria solo facilita el envío y seguimiento de tu solicitud.</p>
        </div>
      </section>

      {list.length > 0 && (
        <>
          <div className="section-heading">
            <div><span className="page-eyebrow">Tus solicitudes</span><h2>{open ? "Tienes una solicitud en revisión" : "Historial de solicitudes"}</h2></div>
          </div>
          <div className="loan-list">
            {list.map((application) => <ApplicationRow key={application.id} application={application} />)}
          </div>
          {open && (
            <p className="page-note">Peso a Peso te contactará con la decisión de tu solicitud actual. Podrás enviar una nueva solicitud en cuanto esa se resuelva.</p>
          )}
        </>
      )}
      <RegulatedNote kind="credit" />
    </div>
  );
}

function ApplicationRow({ application }: { application: LoanApplication }) {
  return (
    <Link to={`/credito/solicitudes/${application.id}`} className="loan-row">
      <div className="loan-row-main">
        <strong translate="no">{application.folio}</strong>
        <span>Enviada {formatTimestamp(application.submittedAt)}</span>
      </div>
      <div className="loan-row-amount" translate="no"><strong>{formatMxn(application.amount)}</strong><span>{application.termMonths} meses</span></div>
      <LoanStatusTag status={application.status} />
      <ChevronRight size={16} aria-hidden="true" />
    </Link>
  );
}
